import moment from 'moment'

export const SHIFT_CARD_HEIGHT = 70
export const SHIFT_CARD_GAP = 8
export const SHIFT_CELL_ADD_PLATE_HEIGHT = 12
export const SHIFT_ROW_GAP = 10
export const SHIFT_COLUMN_WIDTH = 110
export const SHIFT_COLUMN_GAP = 10
export const SHIFT_EMPLOYEE_COLUMN_WIDTH = 280
export const SHIFT_SCHEDULE_VERTICAL_SCROLL_MAX_HEIGHT = 'calc(100vh - 220px)'
export const SHIFT_SCHEDULE_STICKY_TOP_Z_INDEX = 4
export const SHIFT_SCHEDULE_STRETCH_MAX_DAYS = 7

export const hideScrollbarSx = {
	scrollbarWidth: 'none',
	msOverflowStyle: 'none',
	'&::-webkit-scrollbar': {
		display: 'none',
	},
} as const

export const horizontalScrollbarSx = {
	scrollbarWidth: 'thin',
	scrollbarColor: '#B8B8B8 #ECECEC',
	'&::-webkit-scrollbar': {
		height: '4px',
		WebkitAppearance: 'none',
	},
	'&::-webkit-scrollbar-button': {
		display: 'none',
		width: 0,
		height: 0,
	},
	'&::-webkit-scrollbar-button:start:decrement': {
		display: 'none',
		width: 0,
		height: 0,
	},
	'&::-webkit-scrollbar-button:end:increment': {
		display: 'none',
		width: 0,
		height: 0,
	},
	'&::-webkit-scrollbar-track': {
		backgroundColor: '#ECECEC',
		borderRadius: '2px',
	},
	'&::-webkit-scrollbar-thumb': {
		backgroundColor: '#B8B8B8',
		borderRadius: '2px',
	},
	'&::-webkit-scrollbar-thumb:hover': {
		backgroundColor: '#9E9E9E',
	},
} as const

export const shouldStretchShiftColumns = (dateCount: number): boolean =>
	dateCount <= SHIFT_SCHEDULE_STRETCH_MAX_DAYS

export const isPastScheduleDate = (date: number): boolean =>
	moment(date).startOf('day').isBefore(moment().startOf('day'))

export const getSevenDayColumnWidth = (containerWidth: number): number => {
	if (containerWidth <= 0) {
		return SHIFT_COLUMN_WIDTH
	}

	const gaps = 6 * SHIFT_COLUMN_GAP
	return Math.max(
		SHIFT_COLUMN_WIDTH,
		Math.floor((containerWidth - gaps) / SHIFT_SCHEDULE_STRETCH_MAX_DAYS),
	)
}

export const getShiftScheduleGridWidth = (
	dateCount: number,
	columnWidth: number = SHIFT_COLUMN_WIDTH,
): number => dateCount * columnWidth + (dateCount - 1) * SHIFT_COLUMN_GAP

export const getDatesInRange = (
	startDate: number,
	endDate: number,
): moment.Moment[] => {
	const dates: moment.Moment[] = []
	const current = moment(startDate).startOf('day')
	const end = moment(endDate).startOf('day')

	while (current.isSameOrBefore(end)) {
		dates.push(current.clone())
		current.add(1, 'day')
	}

	return dates
}

export const getDateCountInRange = (
	startDate: number,
	endDate: number,
): number => getDatesInRange(startDate, endDate).length

export const getShiftScheduleColumnsSx = (
	dateCount: number,
	columnWidth: number = SHIFT_COLUMN_WIDTH,
) => {
	const stretch = shouldStretchShiftColumns(dateCount)
	const resolvedColumnWidth = stretch ? SHIFT_COLUMN_WIDTH : columnWidth
	const gridWidth = getShiftScheduleGridWidth(dateCount, resolvedColumnWidth)

	return {
		display: 'grid',
		gridTemplateColumns: stretch
			? `repeat(${dateCount}, minmax(${SHIFT_COLUMN_WIDTH}px, 1fr))`
			: `repeat(${dateCount}, ${resolvedColumnWidth}px)`,
		gap: `${SHIFT_COLUMN_GAP}px`,
		width: stretch ? '100%' : `${gridWidth}px`,
		minWidth: stretch ? '100%' : `${gridWidth}px`,
		flexShrink: 0,
	}
}

export const sortCellAssignments = <T extends { id: number }>(
	assignments: T[],
): T[] => [...assignments].sort((left, right) => left.id - right.id)

export const getShiftCellContentHeight = (assignmentCount: number): number => {
	if (assignmentCount <= 0) return SHIFT_CARD_HEIGHT
	return (
		assignmentCount * SHIFT_CARD_HEIGHT + (assignmentCount - 1) * SHIFT_CARD_GAP
	)
}

export const getMaxAssignmentCountInRow = (
	employeeId: number | string,
	dates: number[],
	assignmentCountByCellKey: Map<string, number>,
): number => {
	let maxCount = 0

	dates.forEach(date => {
		const key = `${Number(employeeId)}-${date}`
		maxCount = Math.max(maxCount, assignmentCountByCellKey.get(key) ?? 0)
	})

	return Math.max(maxCount, 1)
}

export const getEmployeeRowHeight = (
	employeeId: number | string,
	dates: number[],
	assignmentCountByCellKey: Map<string, number>,
): number => {
	const slotCount = getMaxAssignmentCountInRow(
		employeeId,
		dates,
		assignmentCountByCellKey,
	)

	return getShiftCellContentHeight(slotCount)
}
